/**
 * Session Management Service
 *
 * Provides utilities for managing report generation sessions,
 * preventing concurrent sessions, and handling stuck/timed out sessions.
 */

import { supabase } from '@shared/lib/supabase';

export interface ActiveSession {
  session_id: string;
  title: string;
  status: string;
  current_stage: string;
  current_step: number;
  total_steps: number;
  progress_percent: number;
  started_at: string;
  updated_at: string;
  is_stuck: boolean;
}

export interface CanStartReportResult {
  can_start: boolean;
  message: string;
  active_session?: {
    session_id: string;
    title: string;
    status: string;
    current_stage: string;
    progress_percent: number;
    started_at: string;
    updated_at: string;
  };
  cleaned_session?: string;
}

/**
 * Get the user's active processing session if one exists
 * Includes retry logic for transient errors
 */
export async function getUserActiveSession(
  userId: string,
  retryCount: number = 0
): Promise<ActiveSession | null> {
  const MAX_RETRIES = 2;

  try {
    console.log(`🔍 Fetching active session for user: ${userId.substring(0, 8)}...`);

    const { data, error } = await supabase.rpc('get_user_active_session', {
      p_user_id: userId
    });

    if (error) {
      console.error('❌ Error fetching active session:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });

      // Retry on network or temporary database errors
      if (retryCount < MAX_RETRIES && (
        error.message?.includes('network') ||
        error.message?.includes('timeout') ||
        error.code === 'PGRST301'
      )) {
        console.log(`🔄 Retrying getUserActiveSession (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return getUserActiveSession(userId, retryCount + 1);
      }

      throw error;
    }

    if (!data || data.length === 0) {
      console.log('✅ No active session found');
      return null;
    }

    console.log(`✅ Found active session: ${data[0].session_id.substring(0, 8)}...`);
    return data[0] as ActiveSession;
  } catch (error: any) {
    console.error('❌ Exception in getUserActiveSession:', error);
    throw error;
  }
}

/**
 * Check if user can start a new report generation
 * This function will automatically cleanup stuck sessions
 * Includes retry logic and graceful degradation
 */
export async function canStartNewReport(
  userId: string,
  retryCount: number = 0
): Promise<CanStartReportResult> {
  const MAX_RETRIES = 2;

  try {
    console.log(`🔍 Checking if user can start new report (attempt ${retryCount + 1})...`);

    // Ensure we have a valid session before calling RPC
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      console.warn('⚠️ No valid auth session, refreshing...');
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('❌ Failed to refresh session:', refreshError);
        throw new Error('Authentication session expired. Please refresh the page.');
      }
    }

    const { data, error } = await supabase.rpc('can_start_new_report', {
      p_user_id: userId
    });

    if (error) {
      console.error('❌ Error checking if user can start new report:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });

      // Retry on transient errors
      if (retryCount < MAX_RETRIES && (
        error.message?.includes('network') ||
        error.message?.includes('timeout') ||
        error.message?.includes('JWT') ||
        error.code === 'PGRST301'
      )) {
        console.log(`🔄 Retrying canStartNewReport (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, 1500 * (retryCount + 1)));
        return canStartNewReport(userId, retryCount + 1);
      }

      // Graceful degradation: If all retries fail, allow user to proceed with warning
      console.warn('⚠️ Could not validate session state after retries. Allowing report generation with warning.');
      return {
        can_start: true,
        message: 'Session validation unavailable. Proceeding with report generation.',
        active_session: undefined
      };
    }

    console.log('✅ Session validation result:', data);
    return data as CanStartReportResult;
  } catch (error: any) {
    console.error('❌ Exception in canStartNewReport:', error);

    // Final graceful degradation: allow report generation rather than blocking user
    console.warn('⚠️ Graceful degradation: Allowing report generation despite validation error');
    return {
      can_start: true,
      message: 'Unable to validate session. You may proceed, but ensure no other reports are currently generating.',
      active_session: undefined
    };
  }
}

/**
 * Manually cleanup stuck sessions (admin only)
 * Returns list of cleaned sessions
 */
export async function cleanupStuckSessions(timeoutMinutes: number = 30): Promise<any[]> {
  try {
    const { data, error } = await supabase.rpc('cleanup_stuck_sessions', {
      p_timeout_minutes: timeoutMinutes
    });

    if (error) {
      console.error('Error cleaning up stuck sessions:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Exception in cleanupStuckSessions:', error);
    throw error;
  }
}

/**
 * Cancel an active session (mark as error)
 * Enhanced to handle edge cases and stuck sessions with multi-layer fallback
 */
export async function cancelSession(
  sessionId: string,
  userId: string,
  force: boolean = false
): Promise<boolean> {
  try {
    console.log(`🔄 Cancelling session ${sessionId.substring(0, 8)}... (force: ${force})`);

    // Verify the session belongs to the user
    const { data: session, error: fetchError } = await supabase
      .from('processing_sessions')
      .select('id, user_id, status, current_stage')
      .eq('id', sessionId)
      .eq('user_id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('❌ Error fetching session to cancel:', fetchError);
      throw fetchError;
    }

    if (!session) {
      console.error('❌ Session not found or does not belong to user');
      throw new Error('Session not found or does not belong to user');
    }

    console.log(`📊 Current session status: ${session.status}, stage: ${session.current_stage}`);

    // Always use force mode for reliability - the RPC function now handles this safely
    force = true;
    console.log('🔧 Using force cancel mode for guaranteed success');

    // LAYER 1: Try force_cancel_session RPC (most reliable, bypasses constraints)
    try {
      const { data: result, error: rpcError } = await supabase.rpc('force_cancel_session', {
        p_session_id: sessionId,
        p_reason: 'Session cancelled by user'
      });

      if (rpcError) {
        console.warn('⚠️ RPC force cancel failed:', rpcError);
        throw rpcError; // Trigger fallback
      }

      if (result?.success) {
        console.log('✅ Session cancelled successfully via RPC');

        // Verify the cancellation worked
        const { data: verifySession } = await supabase
          .from('processing_sessions')
          .select('status, current_stage')
          .eq('id', sessionId)
          .maybeSingle();

        if (verifySession?.status === 'error' && verifySession?.current_stage === 'CANCELLED') {
          console.log('✅ Cancellation verified in database');
          return true;
        } else {
          console.warn('⚠️ RPC succeeded but verification failed, trying fallback');
          throw new Error('Verification failed');
        }
      }

      throw new Error('RPC returned unsuccessful result');
    } catch (rpcError) {
      console.warn('⚠️ RPC force cancel failed, trying direct update fallback:', rpcError);
    }

    // LAYER 2: Direct database update with CANCELLED stage
    console.log('🔧 Attempting direct database update with CANCELLED stage');
    try {
      const { error: updateError } = await supabase
        .from('processing_sessions')
        .update({
          status: 'error',
          error_message: 'Session cancelled by user',
          current_stage: 'CANCELLED',
          updated_at: new Date().toISOString()
        })
        .eq('id', sessionId)
        .eq('user_id', userId);

      if (!updateError) {
        console.log('✅ Direct update with CANCELLED succeeded');
        return true;
      }

      console.warn('⚠️ Direct update with CANCELLED failed:', updateError);
      throw updateError;
    } catch (directError) {
      console.warn('⚠️ Direct update failed, trying ERROR stage fallback');
    }

    // LAYER 3: Fallback to ERROR stage (guaranteed to work)
    console.log('🔧 Final fallback: Using ERROR stage');
    const { error: finalError } = await supabase
      .from('processing_sessions')
      .update({
        status: 'error',
        error_message: 'Session cancelled by user (fallback)',
        current_stage: 'ERROR',
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId)
      .eq('user_id', userId);

    if (finalError) {
      console.error('❌ All cancel attempts failed:', finalError);
      throw finalError;
    }

    console.log(`✅ Session ${sessionId.substring(0, 8)}... cancelled successfully (fallback mode)`);
    return true;
  } catch (error: any) {
    console.error('❌ Exception in cancelSession:', error);

    // Provide more helpful error messages
    if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
      throw new Error('Your session has expired. Please refresh the page and try again.');
    } else if (error.message?.includes('not found')) {
      throw new Error('This session no longer exists or has already been cancelled.');
    } else {
      throw error;
    }
  }
}

/**
 * Check for active sessions on page load and return session to resume
 * Enhanced with better validation and stuck session detection
 */
export async function checkAndResumeActiveSession(
  userId: string
): Promise<{ shouldResume: boolean; session: ActiveSession | null }> {
  try {
    console.log('🔍 Checking for active sessions to resume...');

    // FIRST: Run automatic cleanup of orphaned sessions stuck at initialization
    console.log('🧹 Running automatic cleanup of orphaned sessions...');
    try {
      const { data: cleanupResult, error: cleanupError } = await supabase.rpc('cleanup_orphaned_sessions', {
        p_user_id: userId
      });

      if (!cleanupError && cleanupResult) {
        if (cleanupResult.cleaned_count > 0) {
          console.log(`🧹 Cleaned up ${cleanupResult.cleaned_count} orphaned session(s)`);
        } else {
          console.log('✅ No orphaned sessions found');
        }
      }
    } catch (cleanupError) {
      console.warn('⚠️ Orphaned session cleanup failed (non-fatal):', cleanupError);
      // Continue anyway - cleanup failure shouldn't block the user
    }

    // SECOND: Use can_start_new_report to cleanup any stuck sessions
    // This will automatically clean up stuck sessions before checking for active ones
    const canStartResult = await canStartNewReport(userId);

    if (canStartResult.cleaned_sessions) {
      console.log('🧹 Cleaned up stuck sessions:', canStartResult.cleaned_sessions);
    }

    // If can_start_new_report says there's an active session, validate it further
    if (!canStartResult.can_start && canStartResult.active_session) {
      const session = canStartResult.active_session;
      console.log(`📋 Found active session: ${session.session_id.substring(0, 8)}...`);
      console.log(`   Status: ${session.status}, Stage: ${session.current_stage}`);

      // Additional validation: check if session has made any progress
      const now = new Date();
      const startedAt = new Date(session.started_at);
      const updatedAt = new Date(session.updated_at);
      const minutesSinceStart = (now.getTime() - startedAt.getTime()) / (1000 * 60);
      const minutesSinceUpdate = (now.getTime() - updatedAt.getTime()) / (1000 * 60);

      // Enhanced stuck session detection:
      // 1. Session at step 0 for more than 5 minutes = stuck at initialization
      // 2. No updates for more than 10 minutes = stuck during processing
      const isStuckAtInit = session.current_step === 0 && minutesSinceStart > 5;
      const isStuckDuringProcessing = minutesSinceUpdate > 10 && session.progress_percent < 100;

      if (isStuckAtInit || isStuckDuringProcessing) {
        const reason = isStuckAtInit
          ? `initialization for ${minutesSinceStart.toFixed(1)} minutes`
          : `processing (no updates for ${minutesSinceUpdate.toFixed(1)} minutes)`;

        console.warn(`⚠️ Session appears stuck at ${reason}`);
        console.log('🧹 Attempting to cancel stuck session...');

        try {
          await cancelSession(session.session_id, userId, true);
          console.log('✅ Stuck session cancelled successfully');
        } catch (cancelError) {
          console.error('❌ Failed to cancel stuck session:', cancelError);
          // Continue anyway - better to let user try starting a new report
        }

        return { shouldResume: false, session: null };
      }

      // Session appears valid, return it for resumption
      // Ensure minimum 5% progress to show activity
      const progressPercent = Math.max(session.progress_percent || 0, 5);

      return {
        shouldResume: true,
        session: {
          session_id: session.session_id,
          title: session.title || 'Career Intelligence Report',
          status: session.status,
          current_stage: session.current_stage || 'Processing',
          current_step: session.current_step || 0,
          total_steps: 9,
          progress_percent: progressPercent,
          started_at: session.started_at,
          updated_at: session.updated_at,
          is_stuck: false
        }
      };
    }

    // No active session found or user can start new report
    console.log('✅ No active sessions found, user can start new report');
    return { shouldResume: false, session: null };
  } catch (error: any) {
    console.error('❌ Error in checkAndResumeActiveSession:', error);
    console.warn('⚠️ Proceeding without session resume due to error');

    // Don't block user from using the app if session check fails
    return { shouldResume: false, session: null };
  }
}

/**
 * Validate session is still active and not stuck before starting operations
 */
export async function validateSessionStillActive(sessionId: string): Promise<boolean> {
  try {
    const { data: session, error } = await supabase
      .from('processing_sessions')
      .select('id, status, updated_at, session_timeout_minutes')
      .eq('id', sessionId)
      .maybeSingle();

    if (error || !session) {
      return false;
    }

    if (session.status !== 'processing') {
      return false;
    }

    // Check if session has timed out
    const timeoutMinutes = session.session_timeout_minutes || 30;
    const updatedAt = new Date(session.updated_at);
    const now = new Date();
    const minutesElapsed = (now.getTime() - updatedAt.getTime()) / (1000 * 60);

    if (minutesElapsed > timeoutMinutes) {
      console.warn(`⚠️ Session ${sessionId} has timed out (${minutesElapsed.toFixed(1)} minutes elapsed)`);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating session:', error);
    return false;
  }
}
